import React, { useState, useEffect, useCallback } from "react";
import { useUser } from "../contexts/UserContext";
import { useUserSettings } from "../contexts/UserSettingsContext";
import { useAchievements } from "../contexts/AchievementContext";
import apiService from "../services/api";
import HelpDisclosure from "../components/HelpDisclosure";
import ModalPortal from "../components/ModalPortal";
import shareUtils from "../utils/shareUtils";
import { ReactComponent as AddIcon } from "../assets/icons/add.svg";
import { ReactComponent as CancelIcon } from "../assets/icons/cancel.svg";
import { ReactComponent as PrintIcon } from "../assets/icons/print.svg";
import { ReactComponent as ShareIcon } from "../assets/icons/share.svg";
import "../styles/ShoppingList.css";

const shoppingItemHelp = [
  {
    question: "What goes in quantity?",
    answer: "Use the amount you want to buy, such as 2 lbs, 1 bottle, 500 g, or 3 packs."
  },
  {
    question: "What are notes for?",
    answer: "Use notes for brand, ripeness, store section, substitutions, or reminders for the person shopping."
  },
  {
    question: "Can bought items go to pantry?",
    answer: "Yes. When auto-add to pantry is enabled, completed shopping items can be synced into pantry."
  }
];

const ShoppingList = ({ sidebarOpen }) => {
  const { user } = useUser();
  const { settings } = useUserSettings();
  const { handleApiResponse, isShowingAchievement } = useAchievements();
  const [shoppingLists, setShoppingLists] = useState([]);
  const [stats, setStats] = useState({
    activeLists: 0,
    completedLists: 0,
    totalItems: 0,
    totalCost: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAddListForm, setShowAddListForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [selectedListId, setSelectedListId] = useState(null);
  const [newItemData, setNewItemData] = useState({
    name: '',
    quantity: '',
    notes: ''
  });
  const [bulkItems, setBulkItems] = useState('');
  const [showBulkAddForm, setShowBulkAddForm] = useState(false);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedLists, setSelectedLists] = useState([]);
  const [showEssentialsModal, setShowEssentialsModal] = useState(false);
  const [essentialsText, setEssentialsText] = useState('');
  const [essentialsCount, setEssentialsCount] = useState(0);
  const [missingEssentialsCount, setMissingEssentialsCount] = useState(0);

  // Helpers: build shareable text and printable HTML for a shopping list
  const buildListShareText = (list) => {
    const header = `Shopping List: ${list.name}`;
    const items = (list.items || [])
      .sort((a, b) => {
        if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
        return new Date(a.createdAt) - new Date(b.createdAt);
      })
      .map((item) => `${item.isCompleted ? "[x]" : "[ ]"} ${item.name}${item.quantity ? ` (${item.quantity})` : ""}`)
      .join("\n");
    return `${header}\n\n${items || "(No items)"}`;
  };

  const buildListPrintHtml = (list) => {
    const createdDate = new Date(list.createdAt);
    const itemsHtml = (list.items || [])
      .sort((a, b) => {
        if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
        return new Date(a.createdAt) - new Date(b.createdAt);
      })
      .map((item) => `<li class="${item.isCompleted ? "done" : ""}"> ${item.name}${item.quantity ? ` <em>(${item.quantity})</em>` : ""}</li>`)
      .join("");
    return `
      <h1>Shopping List: ${list.name}</h1>
      <div class="meta">Created ${createdDate.toLocaleDateString()}${list.dueDate ? ` - Due ${new Date(list.dueDate).toLocaleDateString()}` : ""}</div>
      <ul>${itemsHtml || "<li>(No items)</li>"}</ul>
    `;
  };

  const handleShareList = async (list, e) => {
    if (e) e.stopPropagation();
    const text = buildListShareText(list);
    // Try native share first
    const nativeResult = await shareUtils.shareNativeText(list.name, text);
    if (nativeResult.success) {
      showSuccess('Shopping list shared!');
      return;
    }
    // Fallback to WhatsApp (opens if available), otherwise user can copy text manually
    try {
      shareUtils.shareTextToWhatsApp(text);
      showSuccess('Opening WhatsApp to share...');
    } catch (_) {
      // As a last resort, copy to clipboard
      try {
        await navigator.clipboard.writeText(text);
        showSuccess('Shopping list copied to clipboard!');
      } catch (err) {
        showError('Sharing not supported. Please copy manually.');
      }
    }
  };

  const handlePrintList = (list, e) => {
    if (e) e.stopPropagation();
    const html = buildListPrintHtml(list);
    shareUtils.printHtml(html, `Shopping List - ${list.name}`);
  };

  // Custom success notification function
  const showSuccess = useCallback((message) => {
    // If an achievement is being shown, delay the success message
    if (isShowingAchievement) {
      setTimeout(() => {
        showSuccess(message);
      }, 6000); // Wait 6 seconds (5 for achievement + 1 buffer)
      return;
    }
    
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  }, [isShowingAchievement]);

  // Custom error notification function
  const showError = useCallback((message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  }, []);

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
  };

  // Load shopping lists data
  const loadShoppingData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [listsResponse, statsResponse] = await Promise.all([
        apiService.getShoppingLists(),
        apiService.getShoppingListStats()
      ]);
      
      setShoppingLists(listsResponse.lists);
      setStats(statsResponse.stats);
    } catch (error) {
      console.error('Failed to load shopping data:', error);
      showError('Failed to load shopping data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  const loadEssentialsConfig = useCallback(async () => {
    try {
      const response = await apiService.getEssentialsConfig();
      const essentials = Array.isArray(response.essentials) ? response.essentials : [];
      setEssentialsText(essentials.join('\n'));
      setEssentialsCount(response.essentialsCount || 0);
      setMissingEssentialsCount(response.missingCount || 0);
    } catch (error) {
      console.error('Failed to load essentials config:', error);
      showError('Failed to load essentials configuration.');
    }
  }, [showError]);

  // Load data on component mount
  useEffect(() => {
    if (user) {
      loadShoppingData();
      loadEssentialsConfig();
    }
  }, [user, loadShoppingData, loadEssentialsConfig]);

  // Handle search and filter
  const filteredLists = shoppingLists.filter(list => {
    const matchesSearch = list.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filterStatus === 'all') return matchesSearch;
    if (filterStatus === 'active') return matchesSearch && list.status === 'active';
    if (filterStatus === 'completed') return matchesSearch && list.status === 'completed';
    
    return matchesSearch;
  });

  const isEssentialsList = (list) => Boolean(list?.isEssentialList);

  const updateShoppingItemState = useCallback((itemId, updates) => {
    setShoppingLists((currentLists) =>
      currentLists.map((list) => {
        if (!Array.isArray(list.items) || !list.items.some((item) => item.id === itemId)) {
          return list;
        }

        const updatedItems = list.items.map((item) =>
          item.id === itemId ? { ...item, ...updates } : item
        );
        const allItemsCompleted = updatedItems.length > 0 && updatedItems.every((item) => item.isCompleted);

        return {
          ...list,
          status: allItemsCompleted ? 'completed' : 'active',
          items: updatedItems
        };
      })
    );
  }, []);

  // Handle add new list
  const handleAddList = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const formData = new FormData(e.target);
      const listData = {
        name: String(formData.get('name') || '').trim().slice(0, 60),
        description: '',
        dueDate: formData.get('dueDate') || null
      };

      const response = await apiService.createShoppingList(listData);
      
      // Handle achievement notifications
      handleApiResponse(response);
      
      // Show success message with points if earned
      if (response.pointsEarned && response.pointsEarned > 0) {
        showSuccess(`Shopping list created successfully! +${response.pointsEarned} points earned!`);
      } else {
        showSuccess('Shopping list created successfully! (Points already earned for shopping list creation today)');
      }
      
      setShowAddListForm(false);
      loadShoppingData();
    } catch (error) {
      console.error('Failed to create shopping list:', error);
      showError('Failed to create shopping list. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete list
  const handleDeleteList = async () => {
    if (!listToDelete) return;
    if (isEssentialsList(listToDelete)) {
      showError('Essentials list cannot be deleted.');
      setShowDeleteConfirm(false);
      setListToDelete(null);
      return;
    }
    
    setIsLoading(true);
    try {
      await apiService.deleteShoppingList(listToDelete.id);
      showSuccess('Shopping list deleted successfully!');
      setShowDeleteConfirm(false);
      setListToDelete(null);
      loadShoppingData();
    } catch (error) {
      console.error('Failed to delete shopping list:', error);
      showError('Failed to delete shopping list. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle add item to list
  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!selectedListId) return;
    
    setIsLoading(true);
    try {
      await apiService.addShoppingListItem(selectedListId, newItemData);
      showSuccess('Item added to shopping list successfully!');
      setShowAddItemForm(false);
      setNewItemData({ name: '', quantity: '', notes: '' });
      loadShoppingData();
    } catch (error) {
      console.error('Failed to add item:', error);
      showError('Failed to add item. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle toggle item completion
  const handleToggleItem = async (itemId, isCompleted) => {
    let itemToAdd = null;
    for (const list of shoppingLists) {
      if (list.items) {
        const foundItem = list.items.find(item => item.id === itemId);
        if (foundItem) {
          itemToAdd = foundItem;
          break;
        }
      }
    }

    updateShoppingItemState(itemId, { isCompleted });

    try {
      const response = await apiService.updateShoppingListItem(itemId, { isCompleted });
      
      // Handle achievement notifications
      if (response && handleApiResponse) {
        handleApiResponse(response);
      }
      
      // Show points earned popup if points were awarded
      if (response.pointsEarned && response.pointsEarned > 0) {
        showSuccess(`Shopping completed! +${response.pointsEarned} points earned!`);
      }
      
      // If item is being marked as completed and auto-add to pantry is enabled
      if (isCompleted && settings.autoAddToPantry) {
        if (itemToAdd) {
          try {
            // When expiry tracking is disabled, use a far-future date to keep backend compatibility.
            const expiryDate = settings.disableExpiryTracking
              ? new Date('2099-12-31T00:00:00')
              : new Date();
            if (!settings.disableExpiryTracking) {
              expiryDate.setDate(expiryDate.getDate() + settings.pantryDefaultExpiryDays);
            }
            
            // Create pantry item data
            const pantryItemData = {
              name: itemToAdd.name,
              quantity: itemToAdd.quantity,
              location: settings.pantryDefaultLocation,
              expiryDate: expiryDate.toISOString().split('T')[0],
              description: itemToAdd.notes || `Added from shopping list: ${itemToAdd.name}`
            };
            
            // Add to pantry
            await apiService.addPantryItem(pantryItemData);
            showSuccess('Item marked as done and added to pantry!');
          } catch (pantryError) {
            console.error('Failed to add item to pantry:', pantryError);
            showSuccess('Item marked as done, but failed to add to pantry.');
          }
        }
      } else if (isCompleted) {
        showSuccess('Item marked as done!');
      }
      
      // Reload data to get the updated ordering
      await loadShoppingData();
      
      // The backend already handles points for shopping completion
      // No need for additional points logging here
      
    } catch (error) {
      console.error('Failed to update item:', error);
      showError(
        isCompleted
          ? 'Item checked locally, but the server update failed. Tap it again later to retry.'
          : 'Item unchecked locally, but the server update failed. Tap it again later to retry.'
      );
    }
  };

  // Handle delete item
  const handleDeleteItem = async (itemId) => {
    try {
      await apiService.deleteShoppingListItem(itemId);
      showSuccess('Item deleted successfully!');
      loadShoppingData();
    } catch (error) {
      console.error('Failed to delete item:', error);
      showError('Failed to delete item. Please try again.');
    }
  };

  // Handle bulk add items
  const handleBulkAddItems = async (e) => {
    e.preventDefault();
    if (!selectedListId || !bulkItems.trim()) return;
    
    setIsLoading(true);
    try {
      // Parse bulk items (one per line)
      const items = bulkItems
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => {
          // Try to parse "item name - quantity" format
          const parts = line.split(' - ');
          if (parts.length === 2) {
            return {
              name: parts[0].trim(),
              quantity: parts[1].trim(),
              notes: ''
            };
          } else {
            return {
              name: line,
              quantity: '1',
              notes: ''
            };
          }
        });

      // Add all items
      for (const item of items) {
        await apiService.addShoppingListItem(selectedListId, item);
      }

      showSuccess(`Added ${items.length} items to shopping list successfully!`);
      setShowBulkAddForm(false);
      setBulkItems('');
      loadShoppingData();
    } catch (error) {
      console.error('Failed to add bulk items:', error);
      showError('Failed to add items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bulk select toggle
  const handleBulkSelectToggle = () => {
    setBulkSelectMode(!bulkSelectMode);
    setSelectedLists([]);
  };

  // Handle list selection
  const handleListSelect = (listId) => {
    const list = shoppingLists.find((candidate) => candidate.id === listId);
    if (list && isEssentialsList(list)) return;

    if (selectedLists.includes(listId)) {
      setSelectedLists(selectedLists.filter(id => id !== listId));
    } else {
      setSelectedLists([...selectedLists, listId]);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedLists.length === 0) return;

    const protectedIds = new Set(
      shoppingLists.filter((list) => isEssentialsList(list)).map((list) => list.id)
    );
    const deletable = selectedLists.filter((id) => !protectedIds.has(id));

    if (deletable.length === 0) {
      showError('Essentials list cannot be deleted.');
      return;
    }
    
    setIsLoading(true);
    try {
      await apiService.bulkDeleteShoppingLists(deletable);
      showSuccess(`${deletable.length} shopping list(s) deleted successfully!`);
      setBulkSelectMode(false);
      setSelectedLists([]);
      loadShoppingData();
    } catch (error) {
      console.error('Failed to delete shopping lists:', error);
      showError('Failed to delete some shopping lists. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEssentialsModal = async () => {
    await loadEssentialsConfig();
    setShowEssentialsModal(true);
  };

  const handleSaveEssentials = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const parsed = essentialsText
        .split('\n')
        .map((line) => line.split(','))
        .flat()
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

      const response = await apiService.updateEssentialsConfig(parsed);
      setEssentialsCount(response.essentialsCount || 0);
      setMissingEssentialsCount(response.missingCount || 0);
      setShowEssentialsModal(false);
      await loadShoppingData();
      showSuccess(
        parsed.length > 0
          ? `Essentials saved. ${response.missingCount || 0} item(s) are missing and tracked in Essentials list.`
          : 'Essentials cleared. Essentials list will stay hidden until you set items again.'
      );
    } catch (error) {
      console.error('Failed to save essentials:', error);
      showError('Failed to save essentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`shopping-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Header */}
      <div className="shopping-header">
        <div>
          <h1 className="shopping-title">
            <span>{'\uD83D\uDED2'}</span>
            Shopping Lists
          </h1>
          <p className="shopping-subtitle">Organize your grocery shopping and never forget an ingredient</p>
        </div>
        <div className="header-actions">
          <button 
            className={`bulk-select-btn ${bulkSelectMode ? 'active' : ''}`}
            onClick={handleBulkSelectToggle}
            title="Select multiple lists to delete"
          >
            <span>{bulkSelectMode ? '\u2611' : '\u2610'}</span>
            {bulkSelectMode ? 'Cancel Select' : 'Bulk Select'}
          </button>
          <button 
            className="add-item-btn"
            onClick={() => setShowAddListForm(true)}
          >
            <AddIcon className="button-icon" />
            New List
          </button>
          <button
            className="essentials-btn"
            onClick={handleOpenEssentialsModal}
            title="Configure always-needed pantry essentials"
          >
            <span>{'\u2B50'}</span>
            Essentials
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-number green">{stats.activeLists}</div>
          <div className="summary-label">Active Lists</div>
        </div>
        <div className="summary-card">
          <div className="summary-number blue">{stats.totalItems}</div>
          <div className="summary-label">Total Items</div>
        </div>
        <div className="summary-card">
          <div className="summary-number orange">{stats.completedLists}</div>
          <div className="summary-label">Completed</div>
        </div>
        <div className="summary-card">
          <div className="summary-number purple">${stats.totalCost}</div>
          <div className="summary-label">Est. Cost</div>
        </div>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <div className="search-container">
          <input 
            type="text" 
            className="search-input" 
            placeholder="Search shopping lists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="clear-search-btn"
              onClick={clearSearch}
              title="Clear search"
            >
              x
            </button>
          )}
        </div>
        
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            All
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'active' ? 'active' : ''}`}
            onClick={() => setFilterStatus('active')}
          >
            Active
          </button>
          <button 
            className={`filter-btn ${filterStatus === 'completed' ? 'active' : ''}`}
            onClick={() => setFilterStatus('completed')}
          >
            Completed
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {bulkSelectMode && (
        <div className="bulk-actions-bar">
          <div className="bulk-info">
            <span>{selectedLists.length} list(s) selected</span>
          </div>
          <div className="bulk-actions">
            <button 
              className="bulk-delete-btn"
              onClick={handleBulkDelete}
              disabled={selectedLists.length === 0 || isLoading}
            >
              <span>X</span>
              Delete Selected ({selectedLists.length})
            </button>
          </div>
        </div>
      )}

      {/* Shopping Lists */}
      <div className="lists-section">
        <div className="section-header">
          <h2 className="section-title">My Shopping Lists</h2>
          <div className="section-indicators">
            {settings.autoAddToPantry && (
              <div className="auto-add-indicator">
                <span className="indicator-icon">{'\uD83D\uDD01'}</span>
                <span className="indicator-text">Auto-add to pantry enabled</span>
              </div>
            )}
            {essentialsCount > 0 && (
              <div className="essentials-indicator">
                <span className="indicator-icon">{'\u2B50'}</span>
                <span className="indicator-text">
                  {missingEssentialsCount} missing of {essentialsCount} essentials
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="lists-grid">
          {filteredLists.map((list) => {
            const completedItems = list.items ? list.items.filter(item => item.isCompleted).length : 0;
            const totalItems = list.items ? list.items.length : 0;
            const createdDate = new Date(list.createdAt);
            const daysAgo = Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24));
            
            return (
              <div 
                key={list.id} 
                className={`list-card ${isEssentialsList(list) ? 'essentials-list-card' : ''} ${bulkSelectMode ? 'bulk-select-mode' : ''} ${selectedLists.includes(list.id) ? 'selected' : ''}`}
                onClick={bulkSelectMode && !isEssentialsList(list) ? () => handleListSelect(list.id) : undefined}
                style={{ cursor: bulkSelectMode && !isEssentialsList(list) ? 'pointer' : 'default' }}
              >
                {bulkSelectMode && !isEssentialsList(list) && (
                  <div className="bulk-select-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedLists.includes(list.id)}
                      onChange={() => handleListSelect(list.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
                <div className="list-header">
                  <h3 className="list-title">
                    {list.name}
                    {isEssentialsList(list) && (
                      <span className="essentials-lock-badge"> (system)</span>
                    )}
                    {totalItems > 0 && completedItems === totalItems && (
                      <span className="completed-badge"> (completed)</span>
                    )}
                  </h3>
                  <div className="list-actions">
                    <button 
                      className="action-btn action-btn--share"
                      onClick={(e) => handleShareList(list, e)}
                      title="Share List"
                    >
                      <ShareIcon className="shopping-action-icon" />
                    </button>
                    <button 
                      className="action-btn action-btn--print"
                      onClick={(e) => handlePrintList(list, e)}
                      title="Print List"
                    >
                      <PrintIcon className="shopping-action-icon" />
                    </button>
                    {!isEssentialsList(list) && (
                      <button
                        className="action-btn action-btn--bulk"
                        onClick={() => {
                          setSelectedListId(list.id);
                          setShowBulkAddForm(true);
                        }}
                        title="Add Multiple Items"
                      >
                        <AddIcon className="shopping-action-icon" />
                      </button>
                    )}
                    {!isEssentialsList(list) && (
                      <button 
                        className="action-btn action-btn--danger"
                        onClick={() => {
                          setListToDelete(list);
                          setShowDeleteConfirm(true);
                        }}
                        title="Delete List"
                      >
                        <CancelIcon className="shopping-action-icon" />
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="list-stats">
                  {!isEssentialsList(list) && (
                    <div className="stat-item">
                      <span>{'\u23F1'}</span>
                      <span>Created {daysAgo === 0 ? 'today' : `${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`}</span>
                    </div>
                  )}
                  <div className="stat-item">
                    <span>{'\uD83D\uDCE6'}</span>
                    <span>{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                
                <div className="list-items">
                  {list.items && list.items.length > 0 ? (
                    // Sort items: incomplete first, then by creation date
                    list.items
                      .sort((a, b) => {
                        // First sort by completion status (false comes before true)
                        if (a.isCompleted !== b.isCompleted) {
                          return a.isCompleted ? 1 : -1;
                        }
                        // Then by creation date (oldest first)
                        return new Date(a.createdAt) - new Date(b.createdAt);
                      })
                      .map((item) => (
                        <div key={item.id} className="list-item">
                          <input 
                            type="checkbox" 
                            className={`item-checkbox ${item.isCompleted ? 'checked' : ''}`}
                            checked={item.isCompleted}
                            onChange={(e) => handleToggleItem(item.id, e.target.checked)}
                          />
                          <span className={`item-name ${item.isCompleted ? 'completed' : ''}`}>
                            {item.name}
                          </span>
                          <span className="item-quantity">{item.quantity}</span>
                          {!isEssentialsList(list) && (
                            <button
                              className="delete-item-btn"
                              onClick={() => handleDeleteItem(item.id)}
                              title="Delete Item"
                            >
                              <CancelIcon className="shopping-action-icon" />
                            </button>
                          )}
                        </div>
                      ))
                  ) : (
                    <div className="no-items">No items in this list</div>
                  )}
                </div>
                <div className="list-footer">
                  <span className="list-date">
                    {list.dueDate ? `Due: ${new Date(list.dueDate).toLocaleDateString()}` : 'No due date'}
                  </span>
                  <span className="list-progress">{completedItems}/{totalItems} items</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Add New List Modal */}
      {showAddListForm && (
        <div className="ingredient-modal-overlay">
          <div className="ingredient-modal-container">
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">CREATE NEW SHOPPING LIST</h2>
              <div className="ingredient-modal-subheader">
                <span className="quick-kitchen">SHOPPING MANAGEMENT</span>
                <span className="modal-context-note">Organize your shopping</span>
              </div>
              <button 
                className="ingredient-modal-close" 
                onClick={() => setShowAddListForm(false)}
              >
                x
              </button>
            </div>
            <div className="ingredient-modal-content">
              <form onSubmit={handleAddList}>
                <div className="form-group">
                  <label>List Name *</label>
                  <input 
                    type="text" 
                    name="name" 
                    required 
                    maxLength="60"
                    className="form-input"
                    placeholder="e.g., Weekly Groceries"
                  />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input 
                    type="date" 
                    name="dueDate" 
                    className="form-input"
                  />
                </div>
                <div className="ingredient-modal-footer">
                  <button 
                    type="button" 
                    onClick={() => setShowAddListForm(false)}
                    className="ingredient-clear-btn"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="ingredient-apply-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Creating...' : 'Create List'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEssentialsModal && (
        <div className="ingredient-modal-overlay">
          <div className="ingredient-modal-container">
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">ESSENTIALS SETUP</h2>
              <div className="ingredient-modal-subheader">
                <span className="quick-kitchen">SMART ESSENTIALS</span>
                <span className="modal-context-note">Always keep these in pantry</span>
              </div>
              <button
                className="ingredient-modal-close"
                onClick={() => setShowEssentialsModal(false)}
              >
                x
              </button>
            </div>
            <div className="ingredient-modal-content">
              <form onSubmit={handleSaveEssentials}>
                <div className="form-group">
                  <label>Essential items (one per line or comma-separated)</label>
                  <textarea
                    value={essentialsText}
                    onChange={(e) => setEssentialsText(e.target.value)}
                    className="form-textarea"
                    rows="10"
                    placeholder="Milk&#10;Bread&#10;Chicken breast&#10;Eggs"
                  />
                  <div className="form-help">
                    <p><strong>How it works:</strong></p>
                    <p>- If an essential is missing from pantry, it is auto-added to Essentials shopping list.</p>
                    <p>- If an essential pantry item is expired, it is treated as missing.</p>
                    <p>- Essentials shopping list is system-managed and cannot be deleted.</p>
                  </div>
                </div>
                <div className="ingredient-modal-footer">
                  <button
                    type="button"
                    className="ingredient-clear-btn"
                    onClick={() => setShowEssentialsModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ingredient-apply-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save Essentials'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Add Item Modal - New Design */}
      {showAddItemForm && (
        <div className="ingredient-modal-overlay">
          <div className="ingredient-modal-container">
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">ADD ITEM TO LIST</h2>
              <div className="ingredient-modal-subheader">
                <span className="quick-kitchen">SHOPPING MANAGEMENT</span>
                <HelpDisclosure title="Shopping item help" items={shoppingItemHelp} />
              </div>
              <button 
                className="ingredient-modal-close" 
                onClick={() => setShowAddItemForm(false)}
              >
                x
              </button>
            </div>
            <div className="ingredient-modal-content">
              <form onSubmit={handleAddItem}>
                <div className="form-group">
                  <label>Item Name *</label>
                  <input 
                    type="text" 
                    value={newItemData.name}
                    onChange={(e) => setNewItemData({...newItemData, name: e.target.value})}
                    required 
                    className="form-input"
                    placeholder="e.g., Organic Tomatoes"
                  />
                </div>
                <div className="form-group">
                  <label>Quantity *</label>
                  <input 
                    type="text" 
                    value={newItemData.quantity}
                    onChange={(e) => setNewItemData({...newItemData, quantity: e.target.value})}
                    required 
                    className="form-input"
                    placeholder="e.g., 2 lbs"
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea 
                    value={newItemData.notes}
                    onChange={(e) => setNewItemData({...newItemData, notes: e.target.value})}
                    className="form-textarea"
                    placeholder="Optional notes..."
                    rows="3"
                  />
                </div>
                <div className="ingredient-modal-footer">
                  <button 
                    type="button" 
                    onClick={() => setShowAddItemForm(false)}
                    className="ingredient-clear-btn"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="ingredient-apply-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Adding...' : 'Add Item'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Items Modal */}
      {showBulkAddForm && (
        <div className="ingredient-modal-overlay">
          <div className="ingredient-modal-container">
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">ADD MULTIPLE ITEMS</h2>
              <div className="ingredient-modal-subheader">
                <span className="quick-kitchen">BULK SHOPPING</span>
                <span className="modal-context-note">Quick add multiple items</span>
              </div>
              <button 
                className="ingredient-modal-close" 
                onClick={() => setShowBulkAddForm(false)}
              >
                x
              </button>
            </div>
            <div className="ingredient-modal-content">
              <form onSubmit={handleBulkAddItems}>
                <div className="form-group">
                  <label>Items (one per line)</label>
                  <textarea 
                    value={bulkItems}
                    onChange={(e) => setBulkItems(e.target.value)}
                    className="form-textarea"
                    placeholder="Enter items one per line:&#10;Organic Tomatoes - 2 lbs&#10;Chicken Breast - 1.5 lbs&#10;Whole Milk - 1 gal&#10;&#10;Or just item names:&#10;Fresh Basil&#10;Greek Yogurt"
                    rows="12"
                    required
                  />
                  <div className="form-help">
                    <p><strong>Format options:</strong></p>
                    <p>- "Item Name - Quantity" (e.g., "Tomatoes - 2 lbs")</p>
                    <p>- Just item name (default quantity: 1)</p>
                  </div>
                </div>
                <div className="ingredient-modal-footer">
                  <button 
                    type="button" 
                    onClick={() => setShowBulkAddForm(false)}
                    className="ingredient-clear-btn"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="ingredient-apply-btn"
                    disabled={isLoading || !bulkItems.trim()}
                  >
                    {isLoading ? 'Adding Items...' : 'Add Items'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && listToDelete && (
        <ModalPortal>
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header delete-header">
              <h3>Delete Shopping List</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowDeleteConfirm(false)}
              >
                x
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-content">
                <div className="delete-icon">!</div>
                <div className="delete-text">
                  <h4>Are you sure you want to delete this shopping list?</h4>
                  <div className="list-preview">
                    <div className="preview-item">
                      <span className="item-name">{listToDelete.name}</span>
                      <span className="item-details">
                        {listToDelete.items ? listToDelete.items.length : 0} items
                      </span>
                    </div>
                  </div>
                  <p className="delete-warning">This action cannot be undone.</p>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteList}
                  className="btn-danger"
                  disabled={isLoading}
                >
                  {isLoading ? 'Deleting...' : 'Delete List'}
                </button>
              </div>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Achievement-Style Success Notification */}
      {showSuccessMessage && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">OK</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>{successMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccessMessage(false)}>x</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShoppingList;


